import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Flex, IconButton, VStack, useToast, Box, useMediaQuery } from "@chakra-ui/react";
import { Header, InputBox, LoadingOverlay } from "./SharedComponents";
import Message from "./ChatHistoryList";
import {createStreamingChat, sendStreamingMessage, getChatById, editChatTitle, deleteChat, getInitialAppData} from "../services/API";
import { useParams, useNavigate } from "react-router-dom";
import {initializeFormatters,} from "../utils/chat_format_handler";
import {handleSystemPromptChange, handleEditMessage, handleRegenerate, handleEditChat, handleDeleteChat, handleSelectChat, handleModelSelect, handleTemperatureChange, handleMessageSubmit} from "../utils/chat_interface_services";
import { ChatListSidebar } from "./ChatSidebarComponents";
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { stopStream } from "../services/API";

const escapeRegExp = (text = "") => {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
};

const normalizeCitations = (citations = []) => {
  return citations
    .map((citation) => {
      if (typeof citation === "string") {
        const filename = decodeURIComponent(citation.split("/").pop() || "Source");
        return {
          name: filename,
          url: citation,
        };
      }

      return {
        name: citation.name || citation.filename || "Source",
        url: citation.url || citation.link || citation.href,
      };
    })
    .filter((citation) => citation.url);
};

const linkifySources = (content = "", citations = []) => {
  if (!content || typeof content !== "string") return content;

  let updatedContent = content;
  const sources = normalizeCitations(citations);

  const sourceMarkerPattern = /\(Source:\s*([^)]+)\)/gi;
  updatedContent = updatedContent.replace(sourceMarkerPattern, (marker, sourceText) => {
    let linkedSourceText = sourceText;

    sources.forEach((source) => {
      const escapedName = escapeRegExp(source.name);
      const filenamePattern = new RegExp(`(?<!\\[)${escapedName}(?!\\]\\()`, "gi");

      linkedSourceText = linkedSourceText.replace(filenamePattern, (matchedName) => {
        return `[${matchedName}](${source.url})`;
      });
    });

    return `(Source: ${linkedSourceText})`;
  });

  return updatedContent;
};
// Main chat interface component
const ChatInterface = () => {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const [chatHistory, setChatHistory] = useState({
    today: [],
    yesterday: [],
    last_seven_days: [],
    older: [],
  });
  const [isMobile] = useMediaQuery("(max-width: 610px)");
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [modelList, setModelList] = useState([]);
  const [isNewChat, setIsNewChat] = useState(!conversationId);
  const [selectedChat, setSelectedChat] = useState(null);
  const getDefaultSystemPrompt = () => {
    const currentDate = new Date().toLocaleDateString('en-GB');
    return `You are a helpful, friendly assistant designed to provide accurate, concise, and relevant responses. Today is ${currentDate}. Follow the user's request closely. Do not assume a specific domain or document context unless the user explicitly asks for it. For simple greetings, reply briefly and naturally. Mention sources or document names only when you actually used retrieved document context or when the user asks for citations.`;
  };
  const [systemPrompt, setSystemPrompt] = useState(getDefaultSystemPrompt());
  const [temperature, setTemperature] = useState(0.5);
  const [message, setMessage] = useState("");
  const [selectedModel, setSelectedModel] = useState("");
  const [currentMessages, setCurrentMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isWaitingForFirstResponse, setIsWaitingForFirstResponse] = useState(false);
  const [isWaitingForEditResponse, setIsWaitingForEditResponse] = useState(false);
  const [isChatSidebarVisible, setIsChatSidebarVisible] = useState(true);
  const toast = useToast();
  const [chatInfo, setChatInfo] = useState({
    model: "GPT-4o",
    prompt: getDefaultSystemPrompt(),
    temperature: 0.5,
  });
  const [selectedModelCapabilities, setSelectedModelCapabilities] = useState({
    supports_image: true,
    supports_documents: true,
    max_file_size: 512000000,
    supported_file_formats: ["c", "cpp", "cs", "css", "doc", "docx", "go", "html", "java", "js", "json", "md", "pdf", "php", "pptx", "py", "rb", "sh", "tex", "ts", "txt"],
  });
  const [selectedProvider, setSelectedProvider] = useState("");
  const abortControllerRef = useRef(null);
  const messageContainerRef = useRef(null);
  const [isFirstMessageNavigation, setIsFirstMessageNavigation] =
    useState(false);

  // Function to scroll to bottom
  const scrollToBottom = () => {
    if (messageContainerRef.current) {
      messageContainerRef.current.scrollTop =
        messageContainerRef.current.scrollHeight;
    }
  };

  const toggleSidebar = () => {
    setIsSidebarCollapsed(prev => !prev);
  };

  const handleStopStream = async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setIsStreaming(false);
      setIsWaitingForFirstResponse(false);
    }
    
    try {
      const result = await stopStream(conversationId, currentMessages, systemPrompt, selectedModel);
    } catch (error) {
      console.error("Failed to stop stream:", error);
    }
  };

  useEffect(() => {
    if (isMobile !== undefined) {
      setIsSidebarCollapsed(isMobile);
    }
  }, [isMobile]);


  // Add effect to scroll when messages change
  useEffect(() => {
    scrollToBottom();
  }, [currentMessages]);

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const result = await getInitialAppData();
        if (result.success) {
          const { models, chat_titles } = result.data;
          initializeFormatters(models);
          setChatHistory(chat_titles);
          setModelList(models);
          if (!selectedModel) {
            const firstModel = models?.[0]?.models?.[0];
            if (firstModel) {
              setSelectedModel(firstModel.id);
            }
          }
        }
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to load initial data",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialData();
  }, []);

  useEffect(() => {
    if (conversationId) {
      setSelectedChat({ id: conversationId });
    }
  }, [conversationId]);


  const toggleChatSidebar = () => {
    setIsChatSidebarVisible(!isChatSidebarVisible);
  };

  // Add this effect to handle flag reset
  useEffect(() => {
    if (isFirstMessageNavigation) {
      const timer = setTimeout(() => {
        setIsFirstMessageNavigation(false);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []); 

  // Keep the existing effect for loading chat data
  useEffect(() => {
    const loadChatData = async () => {
      if (conversationId && !isFirstMessageNavigation) {
        await handleSelectChatWrapper({ id: conversationId });
      }
    };
    loadChatData();
  }, [conversationId, isFirstMessageNavigation]);

  useEffect(() => {
    abortControllerRef.current = new AbortController();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Memoize filteredChats to prevent unnecessary calculations on every render
  const filteredChats = useMemo(() => {
    return {
      today: chatHistory.today.filter((chat) =>
        chat.title?.toLowerCase().includes(searchQuery.toLowerCase())
      ),
      yesterday: chatHistory.yesterday.filter((chat) =>
        chat.title?.toLowerCase().includes(searchQuery.toLowerCase())
      ),
      last_seven_days: chatHistory.last_seven_days.filter((chat) =>
        chat.title?.toLowerCase().includes(searchQuery.toLowerCase())
      ),
      older: chatHistory.older.filter((chat) =>
        chat.title?.toLowerCase().includes(searchQuery.toLowerCase())
      ),
    };
  }, [searchQuery, chatHistory]);

  const handleNewChat = async () => {
    if (isStreaming) {
      setIsLoading(true);
      try {
        await handleStopStream();
      } catch (error) {
        console.error("Error stopping stream:", error);
      } finally {
        setIsLoading(false);
      }
    }
    setIsNewChat(true);
    setSelectedChat(null);
    setSystemPrompt(getDefaultSystemPrompt());
    setTemperature(0.5);
    setCurrentMessages([]);
    setChatInfo({
      model: selectedModel,
      prompt: getDefaultSystemPrompt(),
      temperature: 0.5,
    });
    navigate("/chat");
  };

  const handleModelSelectWrapper = (modelId) => {
    handleModelSelect({
      modelId,
      modelList,
      setSelectedModel,
      setSelectedProvider,
      setSelectedModelCapabilities,
    });
    setChatInfo((prevInfo) => ({
      ...prevInfo,
      model: modelId,
    }));
  };

  const handleTemperatureChangeWrapper = (value) => {
    handleTemperatureChange(value, setTemperature, setChatInfo);
    setChatInfo((prevInfo) => ({
      ...prevInfo,
      temperature: value,
    }));
  };

  const handleMessageSubmitWrapper = async (images = [], documents = []) => {
    setIsWaitingForFirstResponse(true);
    const finalPrompt = systemPrompt.trim() === ''
      ? getDefaultSystemPrompt()
      : systemPrompt;

    await handleMessageSubmit({
        message,
        images,
        documents,
        abortControllerRef,
        selectedModel,
        isNewChat,
        toast,
        setCurrentMessages,
        setMessage,
        selectedChat,
        systemPrompt: finalPrompt,
        temperature,
        setIsNewChat,
        setSelectedChat,
        navigate,
        getInitialAppData,
        setChatHistory,
        createStreamingChat,
        sendStreamingMessage,
        currentMessages,
        setIsStreaming,
        setIsFirstMessageNavigation,
        setIsWaitingForFirstResponse,
        modelCapabilities: selectedModelCapabilities,
        provider: selectedProvider
    });
  };

  const handleEditChatWrapper = async (chat) => {
    await handleEditChat({
      chat,
      editChatTitle,
      getInitialAppData,
      setChatHistory,
      toast,
    });
  };

  const handleDeleteChatWrapper = async (chat) => {
    await handleDeleteChat({
      chat,
      selectedChat,
      handleNewChat,
      deleteChat,
      getInitialAppData,
      setChatHistory,
      toast,
    });
  };

  const handleSelectChatWrapper = useCallback(async (chat) => {
    if (isMobile) {
      setIsSidebarCollapsed(true);
    }

    try {
      await handleSelectChat({
        chat,
        setIsLoading,
        setSelectedChat,
        setIsNewChat,
        navigate,
        getChatById,
        setSelectedModel,
        setTemperature,
        setSystemPrompt,
        setCurrentMessages,
        toast,
        isStreaming,
        handleStopStream
      });
    } catch (error) {
      console.error("Error in handleSelectChatWrapper:", error);
    }
  }, [isMobile, setIsSidebarCollapsed, setIsLoading, setSelectedChat, setIsNewChat, navigate, setSelectedModel, setTemperature, setSystemPrompt, setCurrentMessages, toast, isStreaming, handleStopStream]);

  const handleEditMessageWrapper = async (messageIndex, newContent) => {
    setIsWaitingForEditResponse(true);
    await handleEditMessage({
      messageIndex,
      newContent,
      currentMessages,
      setCurrentMessages,
      selectedChat,
      selectedModel,
      temperature,
      systemPrompt,
      abortControllerRef,
      sendStreamingMessage,
      setIsStreaming,
      setIsWaitingForEditResponse
    });
  };

  const handleRegenerateWrapper = async (messageIndex) => {
    await handleRegenerate({
      messageIndex,
      currentMessages,
      setCurrentMessages,
      selectedChat,
      selectedModel,
      temperature,
      systemPrompt,
      abortControllerRef,
      sendStreamingMessage,
      setIsStreaming,
    });
  };

  const handleSystemPromptChangeWrapper = (prompt) => {
    handleSystemPromptChange(setSystemPrompt, setChatInfo, prompt, selectedModel);
    setChatInfo((prevInfo) => ({
      ...prevInfo,
      prompt: prompt,
    }));
  };

  const getModelDisplayName = (modelId) => {
    const provider = modelList.find((p) =>
      p.models.some((m) => m.id === modelId)
    );
    if (provider) {
      const model = provider.models.find((m) => m.id === modelId);
      return model ? model.display_name : modelId;
    }
    return modelId;
  };

  // Memoize the message onChange handler to prevent creating a new function on every render
  const handleMessageChange = useCallback((e) => {
    setMessage(e.target.value);
  }, []);

  // Renders the header component with model and system settings
  const renderHeader = () => (
    <Header
      title={selectedChat?.title || ""}
      info={
        !isNewChat
          ? {
              model: selectedModel,
              prompt: systemPrompt,
              temperature: temperature,
            }
          : null
      }
      isNewChat={isNewChat}
      modelList={modelList}
      onModelSelect={handleModelSelectWrapper}
      systemPrompt={systemPrompt}
      onSystemPromptChange={handleSystemPromptChangeWrapper}
      temperature={temperature}
      onTemperatureChange={handleTemperatureChangeWrapper}
      selectedModel={selectedModel}
      onNewChat={handleNewChat}
    />
  );

  // Custom scrollbar styles for the message container
  const scrollbarStyles = {
    "&::-webkit-scrollbar": {
      width: "6px", 
    },
    "&::-webkit-scrollbar-track": {
      background: "#f1f5f9",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "#3498db", 
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: "#2e86c1", 
    },
  };

  // Memoize messages array
  const renderMessageContainer = () => {
    const messages = useMemo(
      () => {
        const filteredMessages = (currentMessages || []).filter((msg) => (msg.role !== "system" && msg.role !== "developer"));
        if (isWaitingForFirstResponse) {
          return [...filteredMessages, { role: 'assistant', content: '.' }];
        }
        return filteredMessages;
      },
      [currentMessages, isWaitingForFirstResponse]
    );

    // Memoize renderMessage function
    const renderMessage = useCallback(
      (msg, index) => {

        const isLastMessage =
          msg.role === "user" && (index === messages.length - 2 || index === messages.length - 1);
        const isLastResponse =
          msg.role === "assistant" && index === messages.length - 1;

        const lastMessage = messages[messages.length - 1];
        const hasDocumentsInLastMessage = Boolean(
          lastMessage?.document_names?.length || lastMessage?.documents?.length
        );

        const displayMessage =
          msg.role === "assistant"
            ? {
                ...msg,
                content: linkifySources(msg.content, msg.citations),
              }
            : msg;

        return (
          <Message
            key={msg.id || index}
            message={displayMessage}
            assistantName={getModelDisplayName(selectedModel)}
            onCopy={() => {
              let textToCopy = "";

              // Handle array content (for messages with images)
              if (Array.isArray(msg.content)) {
                textToCopy = msg.content
                  .filter((item) => item.type === "text")
                  .map((item) => item.text)
                  .join("\n");
              } else if (typeof msg.content === "string") {
                // Handle document references
                const docMatch = msg.content.match(
                  /\[DOCUMENT_REFERENCES:{.*?}\]/
                );
                textToCopy = docMatch
                  ? msg.content.replace(docMatch[0], "").trim()
                  : msg.content;
              }

              const textArea = document.createElement("textarea");
              textArea.value = textToCopy;
              textArea.style.position = "fixed";
              textArea.style.left = "-999999px";
              textArea.style.top = "-999999px";
              document.body.appendChild(textArea);
              textArea.focus();
              textArea.select();
              try {
                document.execCommand("copy");
              } catch (err) {
                console.error("Copy failed:", err);
              }
              document.body.removeChild(textArea);
            }}
            onEdit={
              msg.role === "user"
                ? (newContent) => handleEditMessageWrapper(index, newContent)
                : undefined
            }
            onRegenerate={
              msg.role === "assistant"
                ? () => handleRegenerateWrapper(index)
                : undefined
            }
            isLastMessage={isLastMessage}
            isLastResponse={isLastResponse}
            hasDocumentsInLastMessage={hasDocumentsInLastMessage}
            isWaitingForFirstResponse={isWaitingForFirstResponse && isLastResponse}
            isWaitingForEditResponse={isWaitingForEditResponse && isLastResponse}
          />
        );
      },
      [
        messages,
        selectedModel,
        handleEditMessageWrapper,
        handleRegenerateWrapper,
        isWaitingForFirstResponse,
        isWaitingForEditResponse
      ]
    );

    return (
      <VStack
        ref={messageContainerRef}
        flex={1}
        w="full"
        overflowY="auto"
        overflowX="hidden"
        spacing={0}
        align="stretch"
        bg="#ffffff"
        sx={{
          '-webkit-overflow-scrolling': 'touch',
          ...scrollbarStyles
        }}
        maxW="100%"
      >
        {messages.map(renderMessage)}
      </VStack>
    );
  };

  // Renders the input box for user messages and file uploads
  const renderInputBox = () => (
    <InputBox
      placeholder="Ask anything."
      value={message}
      onChange={handleMessageChange}
      onSubmit={handleMessageSubmitWrapper}
      onStop={handleStopStream}
      modelCapabilities={selectedModelCapabilities}
      hideFileUpload={!selectedModel}
      messages={currentMessages}
      provider={selectedProvider}
      isStreaming={isStreaming}
    />
  );

  // Renders the main chat content area
  const renderChatContent = () => (
    <Flex flex={1} direction="column" bg="#ffffff">
      {renderHeader()}
      {renderMessageContainer()}
      {renderInputBox()}
    </Flex>
  );

  // Memoize the sidebar component to prevent re-renders when typing in the input
  const memoizedSidebar = useMemo(() => {
    if (isSidebarCollapsed) return null;
    
    return (
      <ChatListSidebar
        chatHistory={filteredChats}
        onNewChat={handleNewChat}
        selectedChatId={selectedChat?.id}
        onEditChat={handleEditChatWrapper}
        onDeleteChat={handleDeleteChatWrapper}
        onSelectChat={handleSelectChatWrapper}
        searchQuery={searchQuery}
        onSearchChange={(e) => setSearchQuery(e.target.value)}
      />
    );
  }, [
    isSidebarCollapsed,
    filteredChats,
    selectedChat?.id,
    searchQuery,
    isStreaming
  ]);

  // Main render function
  return (
    <Flex h="100vh" h="100dvh" w="100vw" overflow="hidden" position="relative">
      {isLoading && <LoadingOverlay />}
      <Flex>
        {memoizedSidebar}
      </Flex>
  
      {/* Toggle button - positioned outside both sidebars */}
      <IconButton
      icon={isSidebarCollapsed ? <ChevronRightIcon boxSize="1.5rem" /> : <ChevronLeftIcon boxSize="1.5rem" />}
      position="absolute"
      left={isSidebarCollapsed 
          ? "0.5rem" 
          : isMobile 
            ? "calc(100% - 2.5rem)" 
            : "19.125rem"
      }
      top={isMobile ? "0rem" : "50%"}
      transform={isMobile ? "none" : "translateY(-50%)"}
      zIndex={1010}
      variant="unstyled"
      color="#1f2937"
      bg={isMobile ? "rgba(255, 255, 255, 0.9)" : "transparent"}
      borderRadius={isMobile ? "full" : "none"}
      _hover={{ color: "gray.600" }}
      onClick={toggleSidebar}
      aria-label="Toggle sidebar"
      size="lg"
      fontSize="1.5rem"
      p={0}
    />
  
      {/* Main content */}
      <Flex 
        flex={1} 
        direction="column" 
        bg="#ffffff"
        ml={isMobile ? 0 : (isSidebarCollapsed ? 0 : "0")}
        transition="margin-left 0.3s ease"
        w={isMobile && !isSidebarCollapsed ? "0" : "auto"}
        overflow="hidden"
        maxW="100vw"
        minW="0"
      >
        {renderHeader()}
        {renderMessageContainer()}
        {renderInputBox()}
      </Flex>

      {isMobile && !isSidebarCollapsed && (
        <Box
          position="fixed"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg="blackAlpha.700"
          zIndex={900}
          onClick={toggleSidebar}
        />
      )}
    </Flex>
  );
};

export default ChatInterface;
